import React from 'react';

type IconProps = {
  className?: string;
};

/**
 * Same visual language as the first icon set:
 * - Extruded "chip" body (offset dark shape behind a gradient-lit top face).
 * - Neutral surfaces use a cool metallic gradient; accent (#F5401F) elements use a warm
 *   gradient plus a small radial gloss highlight so they read as glossy/lacquered.
 * - Soft drop shadow lifts the whole icon off the page.
 * - All gradient/filter ids are namespaced per icon to avoid collisions when several
 *   icons are mounted on the same page at once.
 */

export const Icons2 = {
  PersonalisedLearningPaths: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="lp-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#8a8a8a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="lp-side" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="lp-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff8a63" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="lp-gloss" cx="0.34" cy="0.28" r="0.75">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="lp-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#lp-shadow)">
        {/* winding path, given thickness with a dark under-stroke */}
        <path
          d="M18 92 C 34 92, 34 70, 48 70 S 62 48, 76 48 S 90 26, 100 26"
          fill="none"
          stroke="#232323"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray="1 14"
          opacity="0.35"
          transform="translate(0,3)"
        />
        <path
          d="M18 92 C 34 92, 34 70, 48 70 S 62 48, 76 48 S 90 26, 100 26"
          fill="none"
          stroke="url(#lp-top)"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray="1 13"
        />

        {/* waypoint beads, each an extruded sphere */}
        <circle cx="18" cy="92" r="8" fill="url(#lp-side)" transform="translate(0,2)" />
        <circle cx="18" cy="92" r="8" fill="url(#lp-top)" />
        <ellipse cx="15" cy="89" rx="3" ry="2.2" fill="#ffffff" opacity="0.3" />

        <circle cx="48" cy="70" r="7" fill="url(#lp-side)" transform="translate(0,2)" />
        <circle cx="48" cy="70" r="7" fill="url(#lp-top)" />
        <ellipse cx="45.5" cy="67.5" rx="2.6" ry="2" fill="#ffffff" opacity="0.3" />

        <circle cx="76" cy="48" r="7" fill="url(#lp-side)" transform="translate(0,2)" />
        <circle cx="76" cy="48" r="7" fill="url(#lp-top)" />
        <ellipse cx="73.5" cy="45.5" rx="2.6" ry="2" fill="#ffffff" opacity="0.3" />

        {/* destination bead, extruded + glossy accent with flag */}
        <circle cx="100" cy="26" r="11" fill="#7a1a06" opacity="0.4" transform="translate(0,3)" />
        <circle cx="100" cy="23" r="11" fill="url(#lp-accent)" />
        <circle cx="96.5" cy="19.5" r="4" fill="url(#lp-gloss)" />
        <line x1="100" y1="23" x2="100" y2="6" stroke="#4d4d4d" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M100 6 L112 10 L100 14 Z" fill="url(#lp-accent)" />
      </g>
    </svg>
  ),

  MyTeamDashboard: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="td-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="td-side" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="td-avatar" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#a8a8a8" />
          <stop offset="1" stopColor="#5c5c5c" />
        </linearGradient>
        <linearGradient id="td-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff8a63" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="td-gloss" cx="0.34" cy="0.28" r="0.75">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="td-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#td-shadow)">
        {/* extruded dashboard panel */}
        <rect x="16" y="21" width="76" height="52" rx="8" fill="url(#td-side)" />
        <rect x="14" y="17" width="76" height="52" rx="8" fill="url(#td-top)" />
        <rect x="14" y="17" width="76" height="18" rx="8" fill="#ffffff" opacity="0.08" />
        <rect x="46" y="69" width="12" height="8" fill="url(#td-side)" />
        <rect x="34" y="77" width="36" height="6" rx="3" fill="url(#td-top)" />

        {/* tiny sparkline of team performance */}
        <polyline
          points="24,52 34,46 44,54 54,40 64,48"
          fill="none"
          stroke="url(#td-accent)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <g fill="url(#td-accent)">
          <circle cx="24" cy="52" r="2.4" />
          <circle cx="44" cy="54" r="2.4" />
          <circle cx="64" cy="48" r="2.4" />
        </g>

        {/* team avatars docked on the panel, overlapping, extruded */}
        <g>
          <circle cx="70" cy="34" r="11" fill="url(#td-side)" transform="translate(0,2)" />
          <circle cx="70" cy="32" r="11" fill="url(#td-avatar)" />
          <circle cx="70" cy="27" r="4.2" fill="#3a3a3a" />
          <path d="M62 40 a8 8 0 0 1 16 0 z" fill="#3a3a3a" />
        </g>
        <g>
          <circle cx="86" cy="38" r="10" fill="#7a1a06" opacity="0.4" transform="translate(0,2)" />
          <circle cx="86" cy="36" r="10" fill="url(#td-accent)" />
          <circle cx="86" cy="31.5" r="3.8" fill="#8f2107" />
          <path d="M79 43 a7 7 0 0 1 14 0 z" fill="#8f2107" />
          <circle cx="83" cy="33" r="3.2" fill="url(#td-gloss)" />
        </g>
      </g>
    </svg>
  ),

  SkillsCompetencyTracking: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="sc-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="sc-side" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="sc-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff8a63" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="sc-gloss" cx="0.35" cy="0.28" r="0.6">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="sc-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#sc-shadow)">
        {/* extruded target rings */}
        <circle cx="52" cy="62" r="34" fill="url(#sc-side)" transform="translate(0,3)" />
        <circle cx="52" cy="59" r="34" fill="url(#sc-top)" />
        <circle cx="52" cy="59" r="24" fill="#2b2b2b" opacity="0.35" />
        <circle cx="52" cy="59" r="24" fill="none" stroke="#232323" strokeWidth="1.6" opacity="0.4" />
        <circle cx="52" cy="59" r="14" fill="url(#sc-side)" />
        <path d="M38 47 a20 20 0 0 1 28 -5" fill="none" stroke="#ffffff" strokeOpacity="0.2" strokeWidth="5" strokeLinecap="round" />

        {/* bullseye + arrow, extruded/glossy */}
        <circle cx="52" cy="59" r="7" fill="#7a1a06" opacity="0.4" transform="translate(0,2)" />
        <circle cx="52" cy="57" r="7" fill="url(#sc-accent)" />
        <circle cx="49.5" cy="54.5" r="2.4" fill="url(#sc-gloss)" />

        <line x1="86" y1="20" x2="55" y2="54" stroke="#4d4d4d" strokeWidth="3.2" strokeLinecap="round" />
        <polygon points="82,12 96,16 88,28 84,20" fill="url(#sc-accent)" />
        <polygon points="60,55 45,55 52,63" fill="url(#sc-accent)" opacity="0.9" />

        {/* rising progress tick, small badge lower right */}
        <polyline
          points="70,86 78,78 84,82 94,68"
          fill="none"
          stroke="url(#sc-accent)"
          strokeWidth="3.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <polygon points="94,68 96,76 88,74" fill="url(#sc-accent)" />
      </g>
    </svg>
  ),

  RealTimeAnalytics: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="rt-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="rt-side" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="rt-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff8a63" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="rt-gloss" cx="0.4" cy="0.3" r="0.7">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="rt-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#rt-shadow)">
        {/* extruded monitor */}
        <rect x="14" y="26" width="72" height="50" rx="7" fill="url(#rt-side)" />
        <rect x="12" y="22" width="72" height="50" rx="7" fill="url(#rt-top)" />
        <rect x="12" y="22" width="72" height="16" rx="7" fill="#ffffff" opacity="0.08" />
        <rect x="42" y="72" width="12" height="8" fill="url(#rt-side)" />
        <rect x="30" y="80" width="36" height="6" rx="3" fill="url(#rt-top)" />

        {/* screen surface */}
        <rect x="20" y="30" width="58" height="34" rx="3" fill="#1f1f1f" />

        {/* live pulse line */}
        <polyline
          points="24,50 34,50 39,38 45,58 51,44 58,50 74,50"
          fill="none"
          stroke="url(#rt-accent)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="74" cy="50" r="3.4" fill="url(#rt-accent)" />

        {/* live badge, extruded glossy */}
        <circle cx="90" cy="24" r="13" fill="#7a1a06" opacity="0.4" transform="translate(0,3)" />
        <circle cx="90" cy="21" r="13" fill="url(#rt-accent)" />
        <circle cx="90" cy="21" r="5" fill="#ffffff" />
        <circle cx="90" cy="21" r="5" fill="url(#rt-accent)" opacity="0.55" />
        <circle cx="86.5" cy="17.5" r="4" fill="url(#rt-gloss)" />
        <circle cx="90" cy="21" r="17" fill="none" stroke="url(#rt-accent)" strokeWidth="1.6" opacity="0.4" />
      </g>
    </svg>
  ),

  HrmsIntegration: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="hi-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="hi-side" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="hi-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff7a54" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="hi-gloss" cx="0.32" cy="0.28" r="0.8">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="hi-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#hi-shadow)">
        {/* extruded ID card */}
        <rect x="16" y="26" width="66" height="70" rx="10" fill="url(#hi-side)" />
        <rect x="14" y="22" width="66" height="70" rx="10" fill="url(#hi-top)" />
        <rect x="14" y="22" width="66" height="24" rx="10" fill="#ffffff" opacity="0.08" />

        {/* head, shaded sphere */}
        <circle cx="47" cy="46" r="13" fill="#232323" opacity="0.5" transform="translate(0,2)" />
        <circle cx="47" cy="46" r="13" fill="url(#hi-top)" />
        <ellipse cx="43" cy="41" rx="6" ry="4.5" fill="#ffffff" opacity="0.22" />

        {/* torso */}
        <path d="M27 88 C27 66 35 58 47 58 C59 58 67 66 67 88 Z" fill="url(#hi-side)" />
        <path d="M27 86 C27 64 35 56 47 56 C59 56 67 64 67 86 Z" fill="url(#hi-top)" />

        {/* sync arrows, extruded ring, showing two-way HRMS integration */}
        <path d="M90 40 A13 13 0 1 1 85 37" fill="none" stroke="#7a1a06" strokeWidth="6.5" strokeLinecap="round" opacity="0.4" transform="translate(0,2)" />
        <path d="M90 38 A13 13 0 1 1 85 35" fill="none" stroke="url(#hi-accent)" strokeWidth="6" strokeLinecap="round" />
        <polygon points="85,29 85,40 95,35" fill="url(#hi-accent)" />
        <circle cx="88" cy="35" r="9" fill="url(#hi-gloss)" />
      </g>
    </svg>
  ),

  GamificationMotivation: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="gm-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#a8a8a8" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="gm-side" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="gm-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffb066" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="gm-gloss" cx="0.35" cy="0.26" r="0.7">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="gm-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#gm-shadow)">
        {/* extruded trophy cup */}
        <path d="M44 34 h24 l-3 30 a10 10 0 0 1 -18 0 z" fill="url(#gm-side)" transform="translate(0,3)" />
        <path d="M44 31 h24 l-3 30 a10 10 0 0 1 -18 0 z" fill="url(#gm-accent)" />
        <ellipse cx="49" cy="37" rx="4.5" ry="7" fill="#ffffff" opacity="0.28" />

        {/* handles */}
        <path d="M44 36 c-12 0 -14 16 0 18" fill="none" stroke="url(#gm-top)" strokeWidth="4.5" strokeLinecap="round" />
        <path d="M68 36 c12 0 14 16 0 18" fill="none" stroke="url(#gm-top)" strokeWidth="4.5" strokeLinecap="round" />

        {/* stem + extruded base */}
        <rect x="53" y="64" width="6" height="10" fill="url(#gm-side)" />
        <rect x="42" y="74" width="28" height="7" fill="url(#gm-side)" transform="translate(0,2)" />
        <rect x="42" y="74" width="28" height="7" rx="2" fill="url(#gm-top)" />
        <rect x="38" y="81" width="36" height="8" fill="url(#gm-side)" transform="translate(0,2)" />
        <rect x="38" y="81" width="36" height="8" rx="2" fill="url(#gm-top)" />

        {/* star medallion on the cup */}
        <polygon points="56,42 58.5,48 65,48.3 60,52.3 61.8,58.5 56,55 50.2,58.5 52,52.3 47,48.3 53.5,48" fill="#ffffff" opacity="0.9" />

        {/* xp / level-up burst, extruded glossy bead */}
        <circle cx="92" cy="30" r="14" fill="#7a1a06" opacity="0.4" transform="translate(0,3)" />
        <circle cx="92" cy="27" r="14" fill="url(#gm-accent)" />
        <ellipse cx="87" cy="21" rx="5" ry="3.6" fill="url(#gm-gloss)" />
        <text x="92" y="33" fontFamily="Arial, sans-serif" fontSize="15" fontWeight={800} fill="#ffffff" textAnchor="middle">
          +1
        </text>
      </g>
    </svg>
  ),
};

export default Icons2;
